import {
  scrollWorkspaceTarget,
  workspaceTargetMargin,
  workspaceTargetVisible,
} from "../../lib/scrollWorkspaceTarget";

/** Only fixed, application-owned markers; URL/user input never becomes a selector. */
export const ASSIST_TARGETS = [
  "start",
  "client",
  "authorization",
  "specification",
  "census",
  "insurers",
  "review",
  "followup",
  "templates",
] as const;
export type AssistTarget = (typeof ASSIST_TARGETS)[number];
export function findAssistTarget(root: HTMLElement, name: AssistTarget) {
  const section = root.querySelector<HTMLElement>(`[data-guide-target="${name}"]`);
  if (!section) return null;
  const candidates = section.matches("button,input,select,textarea,a[href],h1,h2,h3")
    ? [section]
    : [
        ...section.querySelectorAll<HTMLElement>("[data-guide-control]"),
        ...section.querySelectorAll<HTMLElement>(
          "input:not([type=hidden]),select,textarea,button,a[href]",
        ),
        ...(name === "followup" || name === "client"
          ? [...section.querySelectorAll<HTMLElement>("h1,h2,h3")]
          : []),
        ...(name === "followup" && section.textContent?.trim() ? [section] : []),
      ];
  return (
    candidates.find(
      (element) =>
        !element.closest('[hidden],[inert],[aria-hidden="true"],[aria-disabled="true"]') &&
        !element.matches(':disabled,[aria-disabled="true"]') &&
        element.getClientRects().length > 0,
    ) ?? null
  );
}
/** Focus and highlight only. Never click, change values, submit or bypass navigation guards. */
export function markAssistTarget(target: HTMLElement, onVisibility?: (visible: boolean) => void) {
  const previous = target.getAttribute("tabindex");
  const addedTabIndex = !target.matches("button,input,select,textarea,a[href],[tabindex]");
  if (addedTabIndex) target.setAttribute("tabindex", "-1");
  target.setAttribute("data-help-highlight", "true");
  const doc = target.ownerDocument;
  let active = true;
  let frame: number | undefined;
  const align = () => {
    if (!active) return;
    if (frame !== undefined) cancelAnimationFrame(frame);
    scrollWorkspaceTarget(target, workspaceTargetMargin(target, 16));
    frame = requestAnimationFrame(() => {
      frame = undefined;
      if (active) onVisibility?.(workspaceTargetVisible(target));
    });
  };
  const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(align);
  const events = ["wheel", "touchstart", "pointerdown", "keydown"] as const;
  const stop = () => {
    active = false;
    observer?.disconnect();
    if (frame !== undefined) cancelAnimationFrame(frame);
    for (const event of events) doc.removeEventListener(event, stop, true);
  };
  observer?.observe(target);
  for (const bar of doc.querySelectorAll<HTMLElement>("[data-project-anchor-occluder]"))
    observer?.observe(bar);
  for (const event of events) doc.addEventListener(event, stop, { capture: true, passive: true });
  align();
  target.focus({ preventScroll: true });
  return () => {
    stop();
    target.removeAttribute("data-help-highlight");
    if (addedTabIndex && previous === null) target.removeAttribute("tabindex");
  };
}
